import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Globe,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  Search,
  Eye,
  RotateCcw,
  Plus,
  Trash2,
  Edit,
  Copy,
  ChevronLeft,
  ChevronRight,
  Activity,
  Zap,
  TrendingUp,
  Save,
  X,
} from 'lucide-react';

const mockWebhooks = [
  { id: 'wh_001', event_id: 'evt_s_001', type: 'payment_intent.succeeded', payload: { amount: 14900, currency: 'usd' }, processed_at: '2025-01-10T10:30:00Z', status: 'processed', attempts: 1, source: 'Stripe', duration_ms: 142 },
  { id: 'wh_002', event_id: 'evt_s_002', type: 'subscription.created',     payload: { customer: 'cus_123', plan: 'professional' }, processed_at: null, status: 'pending',   attempts: 2, source: 'Stripe', duration_ms: null },
  { id: 'wh_003', event_id: 'evt_s_003', type: 'invoice.payment_failed',   payload: { invoice: 'inv_456', attempt: 2 }, processed_at: null, status: 'failed',    attempts: 5, source: 'Stripe', duration_ms: null },
  { id: 'wh_004', event_id: 'evt_s_004', type: 'customer.subscription.deleted', payload: { customer: 'cus_456' }, processed_at: '2025-01-10T09:55:00Z', status: 'processed', attempts: 1, source: 'Stripe', duration_ms: 89 },
  { id: 'wh_005', event_id: 'evt_s_005', type: 'payment_intent.created',   payload: { amount: 7900 }, processed_at: '2025-01-10T09:40:00Z', status: 'processed', attempts: 1, source: 'Stripe', duration_ms: 201 },
  { id: 'wh_006', event_id: 'evt_s_006', type: 'invoice.payment_succeeded',payload: { invoice: 'inv_789', amount: 9900 }, processed_at: '2025-01-10T09:20:00Z', status: 'processed', attempts: 1, source: 'Stripe', duration_ms: 115 },
  { id: 'wh_007', event_id: 'evt_s_007', type: 'subscription.updated',     payload: { sub_id: 'sub_003', plan: 'enterprise' }, processed_at: null, status: 'failed',    attempts: 3, source: 'Stripe', duration_ms: null },
  { id: 'wh_008', event_id: 'evt_s_008', type: 'charge.refunded',          payload: { charge: 'ch_001', amount: 4900 }, processed_at: '2025-01-10T08:55:00Z', status: 'processed', attempts: 1, source: 'Stripe', duration_ms: 178 },
];

const PAGE_SIZE = 8;

const STATUS_CONFIG = {
  processed: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'Başarılı' },
  failed:    { color: 'bg-red-100 text-red-800',         icon: XCircle,      label: 'Başarısız' },
  pending:   { color: 'bg-yellow-100 text-yellow-800',   icon: Clock,        label: 'Bekliyor' },
};

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

const BLANK_EP = { url: '', events: '', status: 'active', secret: '', description: '' };

const EndpointModal = ({ ep, onSave, onClose }) => {
  const [form, setForm] = useState(ep
    ? { ...ep, events: Array.isArray(ep.events) ? ep.events.join(', ') : ep.events }
    : { ...BLANK_EP });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.url.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      events: form.events.split(',').map(e => e.trim()).filter(Boolean),
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">{ep ? 'Endpoint Düzenle' : 'Yeni Endpoint Ekle'}</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">URL *</label>
            <Input
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              placeholder="https://yoursite.com/api/webhook"
              className="bg-gray-50"
              data-testid="ep-url-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Event Tipleri (virgülle ayır)</label>
            <Input
              value={form.events}
              onChange={e => setForm(f => ({ ...f, events: e.target.value }))}
              placeholder="payment_intent.*, subscription.*, invoice.*"
              className="bg-gray-50"
              data-testid="ep-events-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Açıklama</label>
            <Input
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Bu endpoint hakkında kısa açıklama"
              className="bg-gray-50"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Webhook Secret</label>
            <Input
              value={form.secret}
              onChange={e => setForm(f => ({ ...f, secret: e.target.value }))}
              placeholder="whsec_..."
              className="bg-gray-50"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Durum</label>
            <select
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.url.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="ep-save-btn"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Kaydet
          </Button>
        </div>
      </div>
    </div>
  );
};

const UltraWebhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [epLoading, setEpLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('events');
  const [page, setPage] = useState(1);
  const [selectedWh, setSelectedWh] = useState(null);
  const [epModal, setEpModal] = useState(null); // null | 'new' | {ep object}
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchWebhooks(); }, []);
  useEffect(() => { if (activeTab === 'endpoints') fetchEndpoints(); }, [activeTab]);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/ultra/webhooks`, { headers: { Authorization: `Bearer ${token}` } });
      const items = (res.data.items || []).map(w => ({
        id: w.id,
        event_id: w.event_id,
        type: w.type,
        source: w.provider || 'Stripe',
        status: w.status === 'error' ? 'failed' : (w.status || 'processed'),
        attempts: w.attempts || 1,
        duration_ms: w.duration_ms || null,
        processed_at: w.delivered_at || null,
        payload: w.payload || {},
      }));
      setWebhooks(items.length > 0 ? items : mockWebhooks);
    } catch {
      setWebhooks(mockWebhooks);
    } finally { setLoading(false); }
  };

  const fetchEndpoints = async () => {
    setEpLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/ultra/webhook-endpoints`, { headers: { Authorization: `Bearer ${token}` } });
      setEndpoints(res.data.items || []);
    } catch {
      setEndpoints([]);
    } finally { setEpLoading(false); }
  };

  const handleSaveEndpoint = async (payload) => {
    const token = getToken();
    if (epModal && typeof epModal === 'object' && epModal.id) {
      // Edit
      await axios.patch(`${API}/api/ultra/webhook-endpoints/${epModal.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
    } else {
      // Create
      await axios.post(`${API}/api/ultra/webhook-endpoints`, payload, { headers: { Authorization: `Bearer ${token}` } });
    }
    setEpModal(null);
    fetchEndpoints();
  };

  const handleDeleteEndpoint = async (id) => {
    const token = getToken();
    await axios.delete(`${API}/api/ultra/webhook-endpoints/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setDeleteConfirm(null);
    fetchEndpoints();
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).catch(() => {});
  };

  const filtered = webhooks.filter(w => {
    const q = search.toLowerCase();
    const matchSearch = !q || w.event_id.toLowerCase().includes(q) || w.type.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || w.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const processed = webhooks.filter(w => w.status === 'processed').length;
  const failed    = webhooks.filter(w => w.status === 'failed').length;
  const pending   = webhooks.filter(w => w.status === 'pending').length;
  const successRate = webhooks.length ? Math.round((processed / webhooks.length) * 100) : 0;
  const avgDuration = Math.round(webhooks.filter(w => w.duration_ms).reduce((a, b) => a + b.duration_ms, 0) / (webhooks.filter(w => w.duration_ms).length || 1));

  const kpis = [
    { label: 'Başarı Oranı',   value: `${successRate}%`, icon: TrendingUp,   color: 'bg-emerald-50 text-emerald-600', sub: `${processed} başarılı` },
    { label: 'Başarısız',      value: failed,             icon: XCircle,      color: 'bg-red-50 text-red-600',         sub: 'Yeniden dene' },
    { label: 'Bekleyen',       value: pending,            icon: Clock,        color: 'bg-yellow-50 text-yellow-600',   sub: 'İşlem sırası' },
    { label: 'Ort. Süre',      value: `${avgDuration}ms`, icon: Zap,          color: 'bg-blue-50 text-blue-600',       sub: 'Yanıt süresi' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Webhook Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Stripe ve diğer servislerden gelen event akışı</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchWebhooks}>
            <RefreshCw className="h-4 w-4 mr-1.5" />Yenile
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{k.value}</p>
                    <p className="text-xs text-gray-400 mt-1">{k.sub}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${k.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { key: 'events',    label: 'Event Akışı' },
          { key: 'endpoints', label: "Endpoint'ler" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`webhook-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === t.key ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'events' && (
        <>
          {/* Filters */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Event ID veya tip ara..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9 bg-gray-50 border-gray-200"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                  className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tüm Durumlar</option>
                  <option value="processed">Başarılı</option>
                  <option value="failed">Başarısız</option>
                  <option value="pending">Bekleyen</option>
                </select>
                <span className="text-sm text-gray-400 ml-auto">{filtered.length} event</span>
              </div>
            </CardContent>
          </Card>

          {/* Events Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Webhook Events
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse flex gap-4">
                      <div className="h-4 bg-gray-100 rounded w-32" />
                      <div className="h-4 bg-gray-100 rounded flex-1" />
                      <div className="h-4 bg-gray-100 rounded w-20" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Event ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Tip</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Kaynak</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Durum</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Deneme</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Süre</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">İşlenme</th>
                          <th className="text-left py-3 px-4" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {paged.map(wh => {
                          const sc = STATUS_CONFIG[wh.status] || STATUS_CONFIG.processed;
                          const StatusIcon = sc.icon;
                          return (
                            <tr key={wh.id} className="hover:bg-gray-50 transition-colors">
                              <td className="py-3 px-4 font-mono text-xs text-gray-500">{wh.event_id}</td>
                              <td className="py-3 px-4">
                                <div className="font-medium text-gray-900 text-xs">{wh.type}</div>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant="outline" className="text-xs">{wh.source}</Badge>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${sc.color}`}>
                                  <StatusIcon className="h-3 w-3" />
                                  {sc.label}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-600">{wh.attempts}</td>
                              <td className="py-3 px-4 text-gray-600 text-xs">
                                {wh.duration_ms ? `${wh.duration_ms}ms` : '—'}
                              </td>
                              <td className="py-3 px-4 text-gray-500 text-xs">
                                {wh.processed_at ? new Date(wh.processed_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-gray-700" onClick={() => setSelectedWh(wh)}>
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                  {wh.status === 'failed' && (
                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-orange-500 hover:text-orange-700">
                                      <RotateCcw className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} / {filtered.length} event
                      </span>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        {[...Array(Math.min(totalPages, 5))].map((_, i) => (
                          <Button key={i} variant={page === i + 1 ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(i + 1)}>
                            {i + 1}
                          </Button>
                        ))}
                        <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'endpoints' && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" />
                Webhook Endpoint'leri
              </CardTitle>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setEpModal('new')}
                data-testid="add-endpoint-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" />Endpoint Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {epLoading ? (
              <div className="p-6 space-y-3">
                {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg" />)}
              </div>
            ) : endpoints.length === 0 ? (
              <div className="p-10 text-center text-gray-400">
                <Globe className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Henüz endpoint eklenmemiş</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {endpoints.map(ep => (
                  <div key={ep.id} className="p-5 hover:bg-gray-50 transition-colors" data-testid={`endpoint-row-${ep.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded break-all">{ep.url}</code>
                          <Badge className={ep.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-gray-100 text-gray-600 border-0'}>
                            {ep.status === 'active' ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </div>
                        {ep.description && (
                          <p className="text-xs text-gray-500 mt-1">{ep.description}</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(ep.events || []).map(ev => (
                            <span key={ev} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">{ev}</span>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span>Oluşturuldu: {ep.created_at ? new Date(ep.created_at).toLocaleDateString('tr-TR') : '—'}</span>
                          {ep.success_rate !== undefined && (
                            <span className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" /> Başarı: %{ep.success_rate}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          size="sm" variant="ghost" className="h-8 px-2 text-gray-400 hover:text-blue-600"
                          onClick={() => setEpModal(ep)}
                          data-testid={`edit-ep-${ep.id}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 px-2 text-gray-400 hover:text-gray-600"
                          onClick={() => handleCopyUrl(ep.url)}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 px-2 text-gray-400 hover:text-red-600"
                          onClick={() => setDeleteConfirm(ep)}
                          data-testid={`delete-ep-${ep.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Webhook Detail Modal */}
      {selectedWh && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedWh(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Webhook Detayı</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedWh(null)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                ['Event ID', selectedWh.event_id],
                ['Tip', selectedWh.type],
                ['Kaynak', selectedWh.source],
                ['Durum', (STATUS_CONFIG[selectedWh.status] || STATUS_CONFIG.processed).label],
                ['Deneme Sayısı', selectedWh.attempts],
                ['Yanıt Süresi', selectedWh.duration_ms ? `${selectedWh.duration_ms}ms` : '—'],
                ['İşlenme Zamanı', selectedWh.processed_at ? new Date(selectedWh.processed_at).toLocaleString('tr-TR') : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500 font-medium">{label}</span>
                  <span className="text-gray-900 font-mono text-xs">{value}</span>
                </div>
              ))}
              <div className="mt-3">
                <p className="text-gray-500 font-medium mb-1.5">Payload</p>
                <pre className="bg-gray-50 rounded-lg p-3 text-xs font-mono text-gray-800 overflow-auto max-h-40">
                  {JSON.stringify(selectedWh.payload, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Endpoint Modal */}
      {epModal && (
        <EndpointModal
          ep={epModal === 'new' ? null : epModal}
          onSave={handleSaveEndpoint}
          onClose={() => setEpModal(null)}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Endpoint Sil</h3>
            <p className="text-sm text-gray-500 mb-5">
              <span className="font-mono text-gray-700">{deleteConfirm.url}</span> adresini silmek istediğinizden emin misiniz?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>İptal</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleDeleteEndpoint(deleteConfirm.id)}
                data-testid="confirm-delete-ep-btn"
              >
                Sil
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraWebhooks;
